import { useState, useEffect, useCallback } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Library, Plus, Trash2, Film } from "lucide-react";
import { toast } from "sonner";
import * as api from "@/lib/api";

const isImgUrl = (u) => /\.(gif|png|jpe?g|webp)(\?|$)/i.test((u || "").trim());

export default function LibreriaEserciziDrawer({ open, setOpen, onChanged }) {
  const [items, setItems] = useState([]);
  const [nome, setNome] = useState("");
  const [media, setMedia] = useState("");

  const refresh = useCallback(async () => {
    try { setItems(await api.listExerciseLibrary()); } catch (e) { console.error("Errore libreria:", e); }
  }, []);

  useEffect(() => { if (open) refresh(); }, [open, refresh]);

  const add = async () => {
    if (!nome.trim()) { toast.error("Inserisci il nome dell'esercizio"); return; }
    try {
      await api.saveExercise({ nome: nome.trim(), media: media.trim() });
      setNome(""); setMedia("");
      toast.success("Esercizio aggiunto alla libreria");
      refresh(); onChanged && onChanged();
    } catch (e) { toast.error("Errore nel salvataggio"); }
  };

  const saveRow = async (it) => {
    try { await api.saveExercise(it); onChanged && onChanged(); } catch (e) { toast.error("Errore aggiornamento"); }
  };

  const setRow = (id, patch) => setItems((arr) => arr.map((x) => (x.id === id ? { ...x, ...patch } : x)));

  const remove = async (id) => {
    try { await api.deleteExercise(id); refresh(); onChanged && onChanged(); } catch (e) { toast.error("Errore eliminazione"); }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="w-full border-white/10 bg-[hsl(var(--card))] text-foreground sm:max-w-md" data-testid="libreria-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 font-display text-foreground">
            <Library className="h-5 w-5 text-primary" /> Libreria Esercizi
          </SheetTitle>
        </SheetHeader>

        <div className="mt-5 space-y-4 overflow-y-auto pb-6" style={{ maxHeight: "calc(100vh - 120px)" }}>
          <section className="panel space-y-2 p-4">
            <div className="text-xs font-semibold text-muted-foreground">Nuovo esercizio</div>
            <input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Nome esercizio (es. Panca piana)" className="lab-input" data-testid="lib-new-nome" />
            <input value={media} onChange={(e) => setMedia(e.target.value)} placeholder="Link GIF o video (YouTube, ...)" className="lab-input" data-testid="lib-new-media" />
            <button onClick={add} className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-primary px-3 py-2.5 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5" data-testid="lib-add-btn">
              <Plus className="h-4 w-4" /> Aggiungi alla libreria
            </button>
          </section>

          <div>
            <h3 className="mb-2 font-display text-sm font-bold">Esercizi salvati <span className="ml-1 rounded-md bg-primary/15 px-1.5 py-0.5 text-[11px] font-bold text-primary">{items.length}</span></h3>
            {items.length === 0 ? (
              <div className="rounded-lg border border-dashed border-white/10 py-8 text-center text-sm text-muted-foreground">Nessun esercizio in libreria</div>
            ) : (
              <div className="space-y-2">
                {items.map((it) => (
                  <div key={it.id} className="flex items-center gap-2 rounded-lg border border-white/10 bg-black/20 p-2" data-testid={`lib-item-${it.id}`}>
                    {isImgUrl(it.media) ? (
                      <img src={it.media} alt="" className="h-12 w-12 shrink-0 rounded-md object-cover" />
                    ) : (
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-black/40 text-muted-foreground"><Film className="h-5 w-5" /></div>
                    )}
                    <div className="min-w-0 flex-1 space-y-1">
                      <input value={it.nome} onChange={(e) => setRow(it.id, { nome: e.target.value })} onBlur={() => saveRow(it)} className="w-full rounded bg-black/30 px-2 py-1 text-sm font-semibold outline-none focus:ring-1 focus:ring-primary/40" data-testid={`lib-nome-${it.id}`} />
                      <input value={it.media || ""} onChange={(e) => setRow(it.id, { media: e.target.value })} onBlur={() => saveRow(it)} placeholder="Link GIF/video" className="w-full rounded bg-black/30 px-2 py-1 text-[11px] text-muted-foreground outline-none focus:ring-1 focus:ring-primary/40" data-testid={`lib-media-${it.id}`} />
                    </div>
                    <button onClick={() => remove(it.id)} className="shrink-0 text-muted-foreground transition-colors hover:text-destructive" title="Elimina" data-testid={`lib-remove-${it.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
